/**
 * 검색 결과의 차례 — **「가까운 날짜 우선」** (2026-09-30 사용자 결정).
 *
 * ## ⛔ 고치는 것 — 주석과 코드가 «반대»였다
 *
 * ```
 * EventSearch.tsx:116  // 최신순이 아니라 «가까운 날짜 우선» — "지금 찾는 그 회의" 는 대개 가까운 날짜다.
 *                :117  matched.sort((a, b) => a.startAt.localeCompare(b.startAt));   ⛔ 그냥 «오름차순»
 * ```
 * ⇒ 주석이 뜻한 것은 **「지금과 가까운」**인데, 코드는 **「이른 것부터」**였다.
 *   창이 ±170일이라 **「170일 전」 것이 맨 앞**에 서고, 상한이 `MAX_RESULTS = 30` 이라
 *   ⛔ **30건을 넘으면 다가오는 일정이 «영영 안 보인다».**
 *
 * ⇒ 사용자 결정으로 **주석이 맞고 코드가 틀렸다** — 코드를 주석에 맞춘다.
 *
 * ## ⚠ 그래서 지난 것과 다가올 것이 «섞인다»
 *
 * 그것이 「가까운 날짜 우선」의 뜻이므로 의도한 것이다. 다만 그 결과로
 * **줄에 「지난 것인가 다가올 것인가」가 보여야** 한다 — 안 보이면 사람이 지난 회의를
 * 다가올 것으로 읽는다. 그 표시는 **이 판에 없다**(따로 올려 두었다).
 */

/**
 * 「지금」과 가까운 것이 먼저.
 *
 * @param nowMs 기준 시각. ⚠ **부르는 쪽이 넣는다** — 안에서 `Date.now()` 를 부르면
 *   비교 도중 값이 바뀔 수 있고, 무엇보다 **시험이 시각을 못 박지 못한다.**
 *
 * ★ 같은 거리면 **다가올 것이 먼저**다. 「오늘 오전 9시」와 「오늘 오전 11시」가 10시에
 *   똑같이 한 시간 떨어져 있을 때, 사람이 찾는 것은 대개 **아직 안 지난 쪽**이다.
 */
/**
 * 그 일정이 **이미 지났나** — 「가까운 날짜 우선」이 지난 것과 다가올 것을 «섞기» 때문에 필요하다.
 *
 * ## ⭐ 「끝난 시각」으로 가른다 — 시작이 아니라
 *
 * 회의가 아직 진행 중이면 사람은 그것을 「지난 것」이라 하지 않는다.
 * ⇒ `endAt` 이 지금보다 «앞»이어야 지난 것이다.
 *
 * ★ 그리고 그 덕에 **종일 일정이 저절로 맞는다** — 종일은 `endAt` 이 그날 끝이라
 *   **오늘의 종일 일정은 하루 내내 「안 지난 것」**이 된다. 시작으로 갈랐으면
 *   「오늘 종일 일정이 아침부터 지남으로 보인다」는 보고가 왔을 것이다.
 *
 * ⚠ 못 읽는 시각이면 **「지남」이라 하지 않는다** — 모를 때 꼬리표를 붙이면 거짓이 될 수 있고,
 *   안 붙이면 그저 표시가 없을 뿐이다. 틀리는 방향을 고른 것이다.
 */
export function isPast(ev: { endAt?: string | null }, nowMs: number): boolean {
  if (!ev.endAt) return false;
  const t = Date.parse(ev.endAt);
  return !Number.isNaN(t) && t < nowMs;
}

export function byNearest(nowMs: number) {
  return (a: { startAt: string }, b: { startAt: string }): number => {
    const ta = Date.parse(a.startAt);
    const tb = Date.parse(b.startAt);
    // ⚠ 못 읽는 시각은 «맨 뒤»로 — 앞에 세우면 읽을 수 있는 것을 가린다.
    if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
    if (Number.isNaN(ta)) return 1;
    if (Number.isNaN(tb)) return -1;

    const da = Math.abs(ta - nowMs);
    const db = Math.abs(tb - nowMs);
    if (da !== db) return da - db;

    // 같은 거리 — 다가올 것(>= 지금)이 먼저.
    const aFuture = ta >= nowMs;
    const bFuture = tb >= nowMs;
    if (aFuture !== bFuture) return aFuture ? -1 : 1;
    return 0;
  };
}
